import { FC, Fragment } from 'react'
import { GlobalStyles } from 'twin.macro'
import { graphql, PageProps } from 'gatsby'
import Seo from '../components/Seo'
import Header from '../components/Header'
import Footer from '../components/Footer'
import LeadSectionHero from '../components/LandingPage/LeadSectionHero'
import AlgorithmSection from '../components/LandingPage/AlgorithmSection'
import HowItWorks from '../components/LandingPage/HowItWorks'
import WhatsIncluded from '../components/LandingPage/WhatsIncluded'
import TakingControl from '../components/LandingPage/TakingControl'
import TakingControl2 from '../components/LandingPage/TakingControl2'
import FAQSection from '../components/LandingPage/FAQSection'
import KnowYourRisk from '../components/LandingPage/KnowYourRisk'
import Community from '../components/LandingPage/Community'
import PartnerLogos from '../components/LandingPage/PartnerLogos'
import PressLogos from '../components/LandingPage/PressLogos'
import PuttingPrevention from '../components/LandingPage/PuttingPrevention'
import OurStory from '../components/LandingPage/OurStory'
import ClinicalTeamSection from '../components/LandingPage/ClinicalTeamSection'
import HereForYou from '../components/LandingPage/HereForYou'
import RiskAssessmentSection from '../components/LandingPage/RiskAssessmentSection'
import GetStarted from '../components/LandingPage/GetStarted'
import IfCardSection from '../components/LandingPage/IfCardSection'
import AddressYourConcernsSection from '../components/LandingPage/AddressYourConcernsSection'
import { PrismicHomepageData } from '../types/prismic'
import CircleStats from '../components/LandingPage/CircleStats'
import Whitepapers from '../components/LandingPage/Whitepapers'
import Solutions from '../components/LandingPage/Solutions'
import HalfTextHalfImage from '../components/LandingPage/HalfTextHalfImage'

const Homepage: FC<PageProps<PrismicHomepageData>> = ({ data, location }) => {
  const pageData = data.prismicHomepage.data
  const socialMediaLinks = data.prismicSocialMediaLinks.data.links || []

  const pageProps = {
    pagePath: location.pathname,
    pageTitle: pageData.lead_section_header.text || '',
  }

  return (
    <Fragment>
      <GlobalStyles />
      <Seo
        title={pageData.metadata_title || 'Home'}
        description={pageData.metadata_description}
        image={pageData.metadata_image?.url}
      />
      <Header />

      {/* Section 1 - Hero */}
      <LeadSectionHero
        header={pageData.lead_section_header}
        subhead={pageData.lead_section_subhead}
        image={pageData.lead_section_image}
        partnerLogo={undefined}
        cta={{
          text: pageData.lead_section_cta_text,
          link: pageData.lead_section_cta_link,
          text2: pageData.lead_section_cta_2_text,
          link2: pageData.lead_section_cta_2_link,
          subtext: pageData.lead_section_cta_subtext,
        }}
        headerRichTextComponents={{
          strong: ({ children, text }) => {
            if (text.endsWith('%')) {
              const textWithoutPercent = text.slice(0, -1)
              return (
                <Fragment>
                  <strong tw="uppercase font-object line-height[1] text-gb-red text-[56px] lg:text-[80px]">
                    {textWithoutPercent}
                  </strong>
                  <strong tw="uppercase font-object line-height[1] text-gb-red text-[28px] lg:text-[40px]">
                    %
                  </strong>
                </Fragment>
              )
            }
            return (
              <strong tw="uppercase font-object line-height[1] text-gb-red text-[56px] lg:text-[80px]">
                {children}
              </strong>
            )
          },
        }}
        {...pageProps}
      />

      {pageData.sections.map(({ section }, index) => {
        if (!section?.document) {
          return null
        }
        switch (section.document.type) {
          case 'algorithm_section': {
            const data = section.document.algorithmSectionData
            return <AlgorithmSection key={index} {...data} {...pageProps} />
          }
          case 'how_it_works_section': {
            const data = section.document.howItWorksSectionData
            return <HowItWorks key={index} {...data} />
          }
          case 'whats_included_section': {
            const data = section.document.whatsIncludedSectionData
            return <WhatsIncluded key={index} {...data} {...pageProps} />
          }
          case 'taking_control_section': {
            const data = section.document.takingControlSectionData

            return data.design_2 ? (
              <TakingControl2 key={index} {...data} {...pageProps} />
            ) : (
              <TakingControl key={index} {...data} {...pageProps} />
            )
          }
          case 'faq_section': {
            const data = section.document.faqSectionData
            return <FAQSection key={index} {...data} {...pageProps} />
          }
          case 'know_your_risk_section': {
            const data = section.document.knowYourRiskSectionData
            return <KnowYourRisk key={index} {...data} {...pageProps} />
          }
          case 'community_section': {
            const data = section.document.communitySectionData
            return <Community key={index} {...data} />
          }
          case 'partner_logos_section': {
            const data = section.document.partnerLogosSectionData
            return <PartnerLogos key={index} {...data} />
          }
          case 'press_section': {
            const data = section.document.pressSectionData
            return <PressLogos key={index} {...data} />
          }
          case 'putting_prevention_section': {
            const data = section.document.puttingPreventionSectionData
            return <PuttingPrevention key={index} {...data} />
          }
          case 'our_story_section': {
            const data = section.document.ourStorySectionData
            return <OurStory key={index} {...data} />
          }
          case 'clinical_team_section': {
            const data = section.document.clinicalTeamSectionData
            return <ClinicalTeamSection key={index} {...data} {...pageProps} />
          }
          case 'here_for_you_section': {
            const data = section.document.hereForYouSectionData
            return <HereForYou key={index} {...data} {...pageProps} />
          }
          case 'risk_assessment_section': {
            const data = section.document.riskAssessmentSectionData
            return <RiskAssessmentSection key={index} {...data} />
          }
          case 'get_started_section': {
            const data = section.document.getStartedSectionData
            return <GetStarted key={index} {...data} {...pageProps} />
          }
          case 'if_card_section': {
            const data = section.document.ifCardSectionData
            return <IfCardSection key={index} {...data} {...pageProps} />
          }
          case 'address_your_concerns_section': {
            const data = section.document.addressYourConcernsSectionData
            return <AddressYourConcernsSection key={index} {...data} />
          }
          case 'circle_stats': {
            const data = section.document.circleStatsData
            return <CircleStats key={index} {...data} />
          }
          case 'whitepapers': {
            const data = section.document.whitepapersData
            return <Whitepapers key={index} {...data} />
          }
          case 'solutions': {
            const data = section.document.solutionsData
            return <Solutions key={index} {...data} />
          }
          case 'half_text_half_image': {
            const data = section.document.halfTextHalfImageData
            return <HalfTextHalfImage key={index} {...data} />
          }
          default:
            return null
        }
      })}

      <Footer socialMediaLinks={socialMediaLinks} />
    </Fragment>
  )
}

export default Homepage

export const pageQuery = graphql`
  fragment RichTextFields on PrismicRichTextField {
    text
    html
    richText
  }

  fragment LinkFields on PrismicLinkField {
    link_type
    url
  }

  fragment GatsbyImageFields on PrismicImageField {
    url
    gatsbyImageData
  }

  query HomePage {
    prismicHomepage {
      uid
      data {
        metadata_title
        metadata_description
        metadata_image {
          ...GatsbyImageFields
        }
        lead_section_header {
          ...RichTextFields
        }
        lead_section_subhead {
          ...RichTextFields
        }
        lead_section_image {
          ...GatsbyImageFields
        }
        lead_section_cta_text
        lead_section_cta_link {
          ...LinkFields
        }
        lead_section_cta_2_text
        lead_section_cta_2_link {
          ...LinkFields
        }
        lead_section_cta_subtext
        sections {
          section {
            document {
              ... on PrismicAlgorithmSection {
                type
                algorithmSectionData: data {
                  header
                  subhead {
                    ...RichTextFields
                  }
                  steps {
                    step_header
                    copy
                  }
                  cta_text
                  cta_link {
                    ...LinkFields
                  }
                }
              }
              ... on PrismicHowItWorksSection {
                type
                howItWorksSectionData: data {
                  steps {
                    header
                    copy
                    icon
                  }
                }
              }
              ... on PrismicWhatsIncludedSection {
                type
                whatsIncludedSectionData: data {
                  header {
                    ...RichTextFields
                  }
                  subhead1
                  subhead2
                  image {
                    ...GatsbyImageFields
                  }
                  cta_text
                  cta_link {
                    ...LinkFields
                  }
                  items {
                    icon
                    text
                  }
                }
              }
              ... on PrismicTakingControlSection {
                type
                takingControlSectionData: data {
                  header
                  subhead {
                    ...RichTextFields
                  }
                  image {
                    ...GatsbyImageFields
                  }
                  steps {
                    item_header
                    item_copy
                    icon
                    section_header
                    item_cta_text
                    item_cta_link {
                      ...LinkFields
                    }
                  }
                  cta_text
                  cta_link {
                    ...LinkFields
                  }
                  design_2
                  background_color
                }
              }
              ... on PrismicFaqSection {
                type
                faqSectionData: data {
                  header
                  faqs {
                    question
                    answer
                  }
                  cta_text
                  cta_link {
                    ...LinkFields
                  }
                  background_color
                }
              }
              ... on PrismicKnowYourRiskSection {
                type
                knowYourRiskSectionData: data {
                  header
                  subhead {
                    ...RichTextFields
                  }
                  cta_text
                  cta_link {
                    ...LinkFields
                  }
                  percentage1
                  copy1
                  percentage2
                  percentage_subtext2
                  copy2
                  percentage3
                  percentage_subtext3
                  copy3
                  citation
                }
              }
              ... on PrismicCommunitySection {
                type
                communitySectionData: data {
                  header
                  subhead {
                    ...RichTextFields
                  }
                  testimonials {
                    copy
                    author_name
                    author_age
                    image {
                      ...GatsbyImageFields
                    }
                  }
                  background_color
                }
              }
              ... on PrismicPartnerLogosSection {
                type
                partnerLogosSectionData: data {
                  partners {
                    name
                    logo {
                      ...GatsbyImageFields
                    }
                  }
                }
              }
              ... on PrismicPressSection {
                type
                pressSectionData: data {
                  header
                  press {
                    name
                    logo {
                      ...GatsbyImageFields
                    }
                    link {
                      ...LinkFields
                    }
                  }
                }
              }
              ... on PrismicPuttingPreventionSection {
                type
                puttingPreventionSectionData: data {
                  header
                  subhead
                  percentage_1
                  copy_1
                  percentage_2
                  copy_2
                  image {
                    ...GatsbyImageFields
                  }
                  citation
                }
              }
              ... on PrismicOurStorySection {
                type
                ourStorySectionData: data {
                  header
                  subhead
                  copy
                  image1 {
                    ...GatsbyImageFields
                  }
                  image2 {
                    ...GatsbyImageFields
                  }
                  image3 {
                    ...GatsbyImageFields
                  }
                }
              }
              ... on PrismicClinicalTeamSection {
                type
                clinicalTeamSectionData: data {
                  header
                  subhead {
                    ...RichTextFields
                  }
                  clinicians {
                    image {
                      ...GatsbyImageFields
                    }
                    name
                    copy
                  }
                  cta_text
                  cta_link {
                    ...LinkFields
                  }
                  background_color
                }
              }
              ... on PrismicHereForYouSection {
                type
                hereForYouSectionData: data {
                  header
                  subhead {
                    ...RichTextFields
                  }
                  items {
                    title
                    bullets {
                      ...RichTextFields
                    }
                    image {
                      ...GatsbyImageFields
                    }
                    image_inset {
                      ...GatsbyImageFields
                    }
                    image_tags
                    image_quotes
                    cta_text
                    cta_link {
                      ...LinkFields
                    }
                  }
                  cta_text
                  cta_link {
                    ...LinkFields
                  }
                }
              }
              ... on PrismicRiskAssessmentSection {
                type
                riskAssessmentSectionData: data {
                  header_1 {
                    ...RichTextFields
                  }
                  subhead_1
                  copy_1 {
                    ...RichTextFields
                  }
                  header_2 {
                    ...RichTextFields
                  }
                  subhead_2
                  copy_2 {
                    ...RichTextFields
                  }
                  partners {
                    name
                    logo {
                      ...GatsbyImageFields
                    }
                  }
                }
              }
              ... on PrismicGetStartedSection {
                type
                getStartedSectionData: data {
                  header
                  subhead {
                    ...RichTextFields
                  }
                  ctas {
                    cta_text
                    cta_link {
                      ...LinkFields
                    }
                  }
                  background_color
                }
              }
              ... on PrismicIfCardSection {
                type
                ifCardSectionData: data {
                  prehead
                  header {
                    ...RichTextFields
                  }
                  cards {
                    image {
                      ...GatsbyImageFields
                    }
                    if_title {
                      ...RichTextFields
                    }
                    if_copy {
                      ...RichTextFields
                    }
                    gabbi_title {
                      ...RichTextFields
                    }
                    gabbi_copy {
                      ...RichTextFields
                    }
                  }
                  cta_text
                  cta_link {
                    ...LinkFields
                  }
                }
              }
              ... on PrismicAddressYourConcernsSection {
                type
                addressYourConcernsSectionData: data {
                  header
                  subhead {
                    ...RichTextFields
                  }
                  items {
                    icon {
                      ...GatsbyImageFields
                    }
                    title {
                      ...RichTextFields
                    }
                    subtext
                  }
                  show_header
                  show_item_subtext
                  background_color
                }
              }
              ... on PrismicCircleStats {
                type
                circleStatsData: data {
                  header
                  subhead {
                    ...RichTextFields
                  }
                  item_1_stat
                  item_1_copy
                  item_2_stat
                  item_2_copy
                  item_3_stat
                  item_3_copy
                  source
                }
              }
              ... on PrismicWhitepapers {
                type
                whitepapersData: data {
                  header
                  subhead {
                    ...RichTextFields
                  }
                  whitepapers {
                    title
                    description
                    pdf {
                      ...LinkFields
                    }
                    illustration {
                      ...GatsbyImageFields
                    }
                  }
                }
              }
              ... on PrismicSolutions {
                type
                solutionsData: data {
                  header
                  subhead {
                    ...RichTextFields
                  }
                  existing_solutions {
                    solution_header
                    solution_copy {
                      ...RichTextFields
                    }
                    solution_citation
                    solution_image {
                      ...GatsbyImageFields
                    }
                  }
                }
              }
              ... on PrismicHalfTextHalfImage {
                type
                halfTextHalfImageData: data {
                  header
                  subhead {
                    ...RichTextFields
                  }
                  image {
                    ...GatsbyImageFields
                  }
                  background_color
                }
              }
            }
          }
        }
      }
    }
    prismicSocialMediaLinks {
      data {
        links {
          platform
          link {
            ...LinkFields
          }
        }
      }
    }
  }
`
